// The first time the carving tools come up: one purple box, three little films on a patch of skin. A tap of the knife
// cuts out a traced triangle and the candle shows through; a tap of the fill puts the hole back; the scraper goes over
// a spot four times, a size bigger each time. Next steps through, and that is the only explaining the carve screen does.
// The paper gets the same box the first time it lands: the pen (mirrored), the ruler, the eraser.

import { el, goo } from './dom';
import { ICONS } from '../stencil/icons';

const W = 280, H = 180;
type Draw = (g: CanvasRenderingContext2D, t: number) => [number, number] | null;

interface Step { tool: string; title: string; text: string; secs: number; draw: Draw; tip: [number, number] }

// the triangle the knife cuts and the fill puts back
const TRI: [number, number][] = [[140, 48], [186, 122], [94, 122]];

function skin(g: CanvasRenderingContext2D) {
  g.fillStyle = '#07050a';
  g.fillRect(0, 0, W, H);
  const grad = g.createRadialGradient(110, 40, 10, 140, 90, 190);
  grad.addColorStop(0, '#ffa244');
  grad.addColorStop(0.55, '#e8701a');
  grad.addColorStop(1, '#8a3a08');
  g.fillStyle = grad;
  g.beginPath();
  g.ellipse(140, 92, 132, 84, 0, 0, Math.PI * 2);
  g.fill();
  // ribs
  g.strokeStyle = 'rgba(90, 35, 0, 0.35)';
  g.lineWidth = 3;
  for (const k of [-0.62, -0.25, 0.25, 0.62]) {
    g.beginPath();
    g.ellipse(140, 92, Math.abs(k) * 132, 84, 0, k < 0 ? Math.PI / 2 : -Math.PI / 2, k < 0 ? Math.PI * 1.5 : Math.PI / 2);
    g.stroke();
  }
}

/** candlelight through an opening */
function glow(g: CanvasRenderingContext2D, x: number, y: number, r: number, a = 1) {
  const l = g.createRadialGradient(x, y, 2, x, y, r);
  l.addColorStop(0, `rgba(255, 246, 190, ${a})`);
  l.addColorStop(0.5, `rgba(255, 196, 70, ${a})`);
  l.addColorStop(1, `rgba(220, 110, 20, ${a})`);
  return l;
}

function triPath(g: CanvasRenderingContext2D) {
  g.beginPath();
  g.moveTo(...TRI[0]); g.lineTo(...TRI[1]); g.lineTo(...TRI[2]); g.closePath();
}

const ease = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

// the tool comes in, taps the middle of the shape, and leaves
const IN: [number, number] = [214, 150], MID: [number, number] = [140, 98];
function visit(t: number): [number, number] | null {
  if (t > 2.6) return null;
  const k = t < 1.2 ? 1 - ease(t / 1.2) : ease((t - 2) / 0.6);
  return [MID[0] + (IN[0] - MID[0]) * k, MID[1] + (IN[1] - MID[1]) * k];
}
function tap(g: CanvasRenderingContext2D, t: number) {
  const f = (t - 1.25) / 0.45;
  if (f <= 0 || f >= 1) return;
  g.strokeStyle = `rgba(255, 255, 255, ${0.8 * (1 - f)})`;
  g.lineWidth = 2.5;
  g.beginPath();
  g.arc(MID[0], MID[1], 6 + f * 22, 0, Math.PI * 2);
  g.stroke();
}

const knife: Draw = (g, t) => {
  skin(g);
  const open = ease((t - 1.4) / 0.5);
  g.strokeStyle = `rgba(20, 8, 2, ${0.85 * (1 - open)})`;
  g.lineWidth = 2;
  g.setLineDash([5, 4]);
  g.lineDashOffset = -t * 14;
  triPath(g);
  g.stroke();
  g.setLineDash([]);
  g.lineDashOffset = 0;
  if (open > 0) {
    g.globalAlpha = open;
    triPath(g);
    g.fillStyle = glow(g, 140, 100, 60);
    g.fill();
    g.globalAlpha = 1;
  }
  tap(g, t);
  return visit(t);
};

// four scrapes on one spot, each a size bigger than the last
const SCRAPES = [22, 32, 43, 55];
const scraper: Draw = (g, t) => {
  skin(g);
  const i = Math.min(3, Math.floor(t / 1.15)), f = Math.min(1, (t - i * 1.15) / 0.9);
  const cx = 140, cy = 84;
  const area = (r: number, a: number) => {
    g.beginPath();
    g.ellipse(cx, cy, r, r * 0.8, 0, 0, Math.PI * 2);
    g.fillStyle = glow(g, cx, cy, r * 1.6, a);
    g.fill();
    g.strokeStyle = `rgba(150, 60, 0, ${0.55 * a})`;
    g.lineWidth = 2;
    g.stroke();
  };
  // the earlier scrapes stay, the new one wipes over them a little wider, back and forth
  if (i > 0) area(SCRAPES[i - 1], 0.45 + 0.12 * (i - 1));
  const r = SCRAPES[i], z = f * 6, row = Math.floor(z), across = z - row;
  const x = cx - r + 2 * r * (row % 2 ? 1 - across : across), y = cy - r * 0.8 + (r * 1.6 * (row + across)) / 6;
  g.save();
  g.beginPath();
  g.rect(0, 0, W, Math.min(H, y + 6));
  g.clip();
  area(r, 0.45 + 0.12 * i);
  g.restore();
  // the count and the size, growing with each go
  g.fillStyle = '#fff';
  g.font = '700 22px Grandstander, sans-serif';
  g.fillText(String(i + 1), 16, 32);
  const k = i / 3;
  g.fillStyle = 'rgba(29, 10, 51, 0.85)';
  g.beginPath();
  g.roundRect(14, H - 34, W - 28, 24, 12);
  g.fill();
  g.fillStyle = '#f3e6ff';
  g.font = '600 13px Grandstander, sans-serif';
  g.fillText('Size', 24, H - 17);
  g.fillStyle = '#e8701a';
  g.fillRect(64, H - 23, (W - 120) * k, 3);
  g.fillStyle = 'rgba(243, 230, 255, 0.35)';
  g.fillRect(64 + (W - 120) * k, H - 23, (W - 120) * (1 - k), 3);
  g.fillStyle = '#fff';
  g.beginPath();
  g.arc(64 + (W - 120) * k, H - 22, 6, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.arc(W - 36, H - 22, 3 + k * 6, 0, Math.PI * 2);
  g.fill();
  return f < 1 ? [x, y] : null;
};

const fill: Draw = (g, t) => {
  skin(g);
  const back = ease((t - 1.4) / 0.5);
  g.globalAlpha = 1 - back;
  triPath(g);
  // the candle flickers through the hole until it is filled
  g.fillStyle = glow(g, 140, 100, 56 + 6 * Math.sin(t * 17) * Math.sin(t * 5.3));
  g.fill();
  g.globalAlpha = 1;
  g.strokeStyle = `rgba(90, 35, 0, ${0.3 * (1 - back)})`;
  g.lineWidth = 1.5;
  triPath(g);
  g.stroke();
  tap(g, t);
  return visit(t);
};

const CARVE: Step[] = [
  { tool: 'knife', title: 'Knife', text: 'Click a shape to cut it out, or draw your own cut.', secs: 3.2, draw: knife, tip: [29, 7] },
  { tool: 'fill', title: 'Fill', text: 'Click a hole to fill it back in, or paint over it.', secs: 3.2, draw: fill, tip: [18, 22] },
  { tool: 'scraper', title: 'Scraper', text: 'Scrape away layers to let the light through.', secs: 4.8, draw: scraper, tip: [25, 11] },
];

// ---- the paper's three

const INK = '#15110d';

function paper(g: CanvasRenderingContext2D, mirror = false) {
  g.fillStyle = '#efe6d6';
  g.fillRect(0, 0, W, H);
  if (!mirror) return;
  // the right half under the green glass, the fold dashed down the middle
  g.fillStyle = 'rgba(150, 210, 170, 0.22)';
  g.fillRect(W / 2, 0, W / 2, H);
  g.strokeStyle = 'rgba(21, 17, 13, 0.35)';
  g.lineWidth = 1.5;
  g.setLineDash([5, 5]);
  g.beginPath(); g.moveTo(W / 2, 0); g.lineTo(W / 2, H); g.stroke();
  g.setLineDash([]);
}

// an eye drawn freehand on the left, a loop a little wobbly
const eyeAt = (k: number): [number, number] => {
  const a = -Math.PI + k * Math.PI * 2;
  return [84 + 42 * Math.cos(a) + 3 * Math.sin(k * 19), 82 + (Math.sin(a) < 0 ? 30 : 16) * Math.sin(a)];
};

const pen: Draw = (g, t) => {
  paper(g, true);
  const k = ease(t / 2.6);
  g.strokeStyle = INK;
  g.lineWidth = 6;
  g.lineCap = g.lineJoin = 'round';
  for (const side of [1, -1]) {
    g.beginPath();
    for (let j = 0; j <= k; j += 0.01) { const [x, y] = eyeAt(j); side > 0 ? g.lineTo(x, y) : g.lineTo(W - x, y); }
    g.stroke();
  }
  return k < 1 ? eyeAt(k) : null;
};

// the ruler: a click at each corner of a jagged tooth, then a double-tap that closes and fills it
const RULE: [number, number][] = [[60, 60], [220, 60], [190, 128], [160, 92], [130, 136], [100, 96]];
const ruler: Draw = (g, t) => {
  paper(g);
  const each = 0.5, n = Math.min(RULE.length, Math.floor(t / each) + 1), shut = t > RULE.length * each + 0.5;
  if (shut) {
    g.beginPath();
    RULE.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
    g.closePath();
    g.globalAlpha = ease((t - RULE.length * each - 0.5) / 0.3);
    g.fillStyle = INK;
    g.fill();
    g.globalAlpha = 1;
    return t < RULE.length * each + 1 ? RULE[0] : null;
  }
  // the clicks so far, joined by dotted cut lines, and the line from the last to the pointer
  const last = RULE[n - 1], goal = RULE[n % RULE.length], f = ease((t - (n - 1) * each) / each);
  const at: [number, number] = [last[0] + (goal[0] - last[0]) * f, last[1] + (goal[1] - last[1]) * f];
  g.strokeStyle = INK;
  g.lineWidth = 2;
  g.setLineDash([6, 4]);
  g.beginPath();
  RULE.slice(0, n).forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.lineTo(...at);
  g.stroke();
  g.setLineDash([]);
  g.fillStyle = INK;
  for (const [x, y] of RULE.slice(0, n)) { g.beginPath(); g.arc(x, y, 3.5, 0, Math.PI * 2); g.fill(); }
  // the double-tap back on the first corner: two rings
  if (n === RULE.length && f > 0.85) {
    g.strokeStyle = '#e8701a';
    g.lineWidth = 2;
    for (const r of [8, 14]) { g.beginPath(); g.arc(RULE[0][0], RULE[0][1], r, 0, Math.PI * 2); g.stroke(); }
  }
  return at;
};

// the eraser rubs a band through a filled eye
const eraser: Draw = (g, t) => {
  paper(g);
  g.fillStyle = INK;
  g.beginPath();
  for (let j = 0; j <= 1; j += 0.02) { const [x, y] = eyeAt(j); g.lineTo(x + 56, y); }
  g.fill();
  const k = ease((t - 0.3) / 2.2);
  const path = (j: number): [number, number] => [88 + j * 110, 66 + 40 * j + 8 * Math.sin(j * 14)];
  g.strokeStyle = '#efe6d6';
  g.lineWidth = 22;
  g.lineCap = g.lineJoin = 'round';
  g.beginPath();
  for (let j = 0; j <= k; j += 0.01) g.lineTo(...path(j));
  if (k > 0) g.stroke();
  return k > 0 && k < 1 ? path(k) : null;
};

const PAPER: Step[] = [
  { tool: 'pen', title: 'Pen', text: 'Draw freely. Mirror mode copies it to the other side.', secs: 3.6, draw: pen, tip: [6, 30] },
  { tool: 'ruler', title: 'Ruler', text: 'Click from corner to corner, double-tap to close and fill.', secs: 4.6, draw: ruler, tip: [8, 31] },
  { tool: 'eraser', title: 'Eraser', text: 'Rub out ink.', secs: 3.2, draw: eraser, tip: [6, 24] },
];

/** shows the carve screen's three films, one at a time; done() once it is closed */
export const howTo = (done: () => void) => films(CARVE, 'carve', done);
/** the same for the paper */
export const paperHowTo = (done: () => void) => films(PAPER, 'paper', done);

function films(STEPS: Step[], kind: string, done: () => void): HTMLDialogElement {
  const dlg = el('dialog', 'dlg howto purple');
  dlg.dataset.kind = kind;
  const head = el('p', 'howto-h'), text = el('p', 'howto-t');
  const stage = el('div', 'howto-film');
  const cv = el('canvas');
  const dpr = Math.min(2, devicePixelRatio || 1);
  cv.width = W * dpr; cv.height = H * dpr;
  const hand = el('span', 'howto-hand');
  stage.append(cv, hand);
  const dots = el('div', 'howto-dots');
  const next = goo('Next', () => go(at + 1), 'howto-next');
  const skip = el('button', 'howto-skip');
  skip.type = 'button'; skip.textContent = 'Skip';
  skip.addEventListener('click', () => dlg.close());
  dlg.append(head, text, stage, el('div', 'howto-row'));
  dlg.lastElementChild!.append(dots, skip, next);
  document.body.append(dlg);
  const g = cv.getContext('2d')!;
  g.scale(dpr, dpr);
  let at = 0, t0 = performance.now(), raf = 0;
  const go = (i: number) => {
    if (i >= STEPS.length) { dlg.close(); return; }
    at = i;
    t0 = performance.now();
    const s = STEPS[i];
    dlg.dataset.step = s.tool;
    head.textContent = `${i + 1}. ${s.title}`;
    text.textContent = s.text;
    hand.innerHTML = ICONS[s.tool] ?? '';
    dots.innerHTML = STEPS.map((_, j) => `<i${j === i ? ' class="on"' : ''}></i>`).join('');
    next.querySelector('span')!.textContent = i === STEPS.length - 1 ? 'Got it' : 'Next';
  };
  const frame = (now: number) => {
    // the frame's timestamp can be a moment before Next was pressed
    const s = STEPS[at], t = (Math.max(0, now - t0) / 1000) % s.secs;
    const p = s.draw(g, t);
    hand.style.opacity = p ? '1' : '0';
    // the film is drawn at 280 x 180 and shown at whatever width the box allows
    if (p) hand.style.transform = `translate(${(p[0] / W) * cv.clientWidth - s.tip[0]}px, ${(p[1] / H) * cv.clientHeight - s.tip[1]}px)`;
    raf = requestAnimationFrame(frame);
  };
  dlg.addEventListener('close', () => { cancelAnimationFrame(raf); dlg.remove(); done(); });
  go(0);
  dlg.showModal();
  raf = requestAnimationFrame(frame);
  return dlg;
}
